import { certificates } from "@/data/certificates";
import { certificatePathFor, findCertificateBySlug } from "@/data/certificateSlugs";
import { companyPaths, workEnvironmentLanding } from "@/data/company";
import { drugSampleFromPathSlug, drugTestingPaths, privateDrugPackages } from "@/data/drugTesting";
import { findGuideRoute, guideHref } from "@/data/guideRoutes";
import { GOOGLE_REVIEWS_URL } from "@/data/googleReviews";
import { translations, type Lang } from "@/i18n/translations";
import { companyPages } from "@/i18n/companyPages";
import { drugTestingPages } from "@/i18n/drugTestingPages";

export const SCHEMA_ORIGIN = "https://intygslakaren.se";
export const CLINIC_ID = `${SCHEMA_ORIGIN}/#clinic`;
export const WEBSITE_ID = `${SCHEMA_ORIGIN}/#website`;

type JsonLd = Record<string, unknown>;

const CLINIC_GEO = {
  "@type": "GeoCoordinates",
  latitude: 57.70131,
  longitude: 11.95445,
} as const;

const POPULAR_SLUGS = ["hogre-behorighet", "alkolas", "droger-alkohol"] as const;

const clinicDescription = (lang: Lang) =>
  lang === "en"
    ? "Licensed doctors, a physical clinic in Gothenburg and video medical certificate appointments across Sweden when the requirements allow."
    : "Legitimerade läkare, fysisk mottagning i Göteborg och läkarintyg via video i hela Sverige när intygets krav tillåter.";

const stripBrandSuffix = (title: string) =>
  title.replace(/\s*\|\s*Intygsläkaren$/i, "").trim() || title;

/** Parse display prices like "Från 995 kr" / "1 500 kr" / "From 3,000 SEK" into Offer fields. */
export const parsePriceOffer = (
  priceText: string,
  url: string,
  lang: Lang,
): JsonLd | null => {
  const normalized = priceText.replace(/\u00a0/g, " ").trim();
  if (!normalized || /kostnadsförslag|quote on request|offert|kontakt/i.test(normalized)) {
    return null;
  }

  const match = normalized.match(/(\d[\d\s.,]*)/);
  if (!match) return null;

  const digits = match[1].replace(/[^\d]/g, "");
  if (!digits) return null;

  const amount = Number.parseInt(digits, 10);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const isFrom = /från|from/i.test(normalized);

  return {
    "@type": "Offer",
    url,
    priceCurrency: "SEK",
    price: String(amount),
    priceSpecification: isFrom
      ? {
          "@type": "UnitPriceSpecification",
          priceCurrency: "SEK",
          minPrice: amount,
          price: amount,
        }
      : undefined,
    availability: "https://schema.org/InStock",
    description: normalized,
    name: lang === "sv" ? (isFrom ? `Från ${amount} kr` : `${amount} kr`) : normalized,
  };
};

const cleanOffer = (offer: JsonLd | null): JsonLd | null => {
  if (!offer) return null;
  if (offer.priceSpecification === undefined) {
    const { priceSpecification: _, ...rest } = offer;
    return rest;
  }
  return offer;
};

const certFaqGeneric = (lang: Lang): { q: string; a: string }[] =>
  lang === "sv"
    ? [
        {
          q: "Kan jag göra besöket online?",
          a: "Det beror på intygets krav. Många intyg kan utfärdas efter ett videobesök när reglerna tillåter det. Välj besöksform i bokningen så syns de tillgängliga alternativen.",
        },
        {
          q: "Hur snabbt kan jag få en tid?",
          a: "Vi har ofta lediga tider inom några dagar. Välj en tid direkt i onlinebokningen – inga remisser eller mellanhänder behövs.",
        },
        {
          q: "Vad händer om intyget inte kan utfärdas direkt?",
          a: "Läkaren informerar dig vad som behöver kompletteras – till exempel ytterligare underlag, provtagning eller en återkoppling från din behandlande läkare. Du får tydlig information om nästa steg.",
        },
        {
          q: "Hur lång tid tar besöket?",
          a: "De flesta besök tar 15–30 minuter. Intyg som kräver undersökning eller provtagning kan ta lite längre. Du får mer information när du bokar.",
        },
      ]
    : [
        {
          q: "Can I have the appointment online?",
          a: "It depends on the certificate's requirements. Many certificates can be issued after a video consultation when the rules allow it. The available options are shown when you book.",
        },
        {
          q: "How quickly can I get an appointment?",
          a: "We usually have available slots within a few days. Book directly online — no referrals or intermediaries needed.",
        },
        {
          q: "What happens if the certificate cannot be issued straight away?",
          a: "The doctor will tell you what needs to be completed — for example additional documentation, testing, or feedback from your treating doctor. You will receive clear information about the next steps.",
        },
        {
          q: "How long does the appointment take?",
          a: "Most appointments take 15–30 minutes. Certificates requiring an examination or testing may take a little longer. You will receive more information when you book.",
        },
      ];

const faqEntity = (items: { q: string; a: string }[]) =>
  items.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  }));

const faqPageNode = (url: string, items: { q: string; a: string }[]): JsonLd => ({
  "@type": "FAQPage",
  "@id": `${url}#faq`,
  mainEntity: faqEntity(items),
});

export const medicalClinicNode = (lang: Lang): JsonLd => ({
  "@type": "MedicalClinic",
  "@id": CLINIC_ID,
  name: "Intygsläkaren",
  description: clinicDescription(lang),
  url: SCHEMA_ORIGIN,
  legalName: "AG Medical AB",
  identifier: "559282-3685",
  taxID: "559282-3685",
  telephone: "+46317617700",
  email: "info@intygslakaren.se",
  logo: `${SCHEMA_ORIGIN}/intygslakaren-og-image.png`,
  image: `${SCHEMA_ORIGIN}/intygslakaren-og-image.png`,
  areaServed: { "@type": "Country", name: "Sweden" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Järntorgsgatan 8",
    postalCode: "413 30",
    addressLocality: "Göteborg",
    addressRegion: "Västra Götaland",
    addressCountry: "SE",
  },
  geo: CLINIC_GEO,
  hasMap: GOOGLE_REVIEWS_URL,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "17:00",
    },
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: "+46317617700",
      email: "info@intygslakaren.se",
      availableLanguage: ["Swedish", "English"],
      areaServed: "SE",
    },
  ],
  medicalSpecialty: ["PrimaryCare", "OccupationalMedicine"],
  availableLanguage: ["Swedish", "English"],
  currenciesAccepted: "SEK",
  paymentAccepted: "Cash, Credit Card, Debit Card, Swish, Klarna",
  priceRange: "$$",
  sameAs: [GOOGLE_REVIEWS_URL],
});

export const websiteNode = (): JsonLd => ({
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SCHEMA_ORIGIN,
  name: "Intygsläkaren",
  inLanguage: ["sv", "en"],
  publisher: { "@id": CLINIC_ID },
});

type BreadcrumbTrailItem = { name: string; path: string };

const absoluteUrl = (path: string) =>
  path === "/" ? `${SCHEMA_ORIGIN}/` : `${SCHEMA_ORIGIN}${path}`;

const breadcrumbNode = (url: string, trail: BreadcrumbTrailItem[]): JsonLd | null => {
  if (trail.length < 2) return null;
  return {
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: trail.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
};

/**
 * Service catalogs as OfferCatalog (not ItemList).
 * ItemList+Service is parsed as a Carousel rich result, which only allows
 * specific types (Recipe, Course, …) and forbids ListItem.url + ListItem.item together.
 */
const offerCatalogNode = (
  url: string,
  name: string,
  items: { name: string; url: string }[],
): JsonLd => ({
  "@type": "OfferCatalog",
  "@id": `${url}#offercatalog`,
  name,
  numberOfItems: items.length,
  itemListElement: items.map((entry, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Offer",
      url: entry.url,
      itemOffered: {
        "@type": "Service",
        name: entry.name,
        url: entry.url,
        provider: { "@id": CLINIC_ID },
      },
    },
  })),
});

const howToNode = (
  url: string,
  name: string,
  description: string,
  steps: { title: string; text: string }[],
): JsonLd => ({
  "@type": "HowTo",
  "@id": `${url}#howto`,
  name,
  description,
  inLanguage: url.includes("/en/") || url.endsWith("/en") ? "en" : "sv",
  step: steps.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.title,
    text: step.text,
  })),
});

const serviceShell = ({
  url,
  name,
  description,
  offer,
  audienceType,
  areaSweden = true,
}: {
  url: string;
  name: string;
  description: string;
  offer?: JsonLd | null;
  audienceType: string;
  areaSweden?: boolean;
}): JsonLd => {
  const node: JsonLd = {
    "@type": "Service",
    "@id": `${url}#service`,
    name,
    description,
    url,
    provider: { "@id": CLINIC_ID },
    areaServed: areaSweden
      ? { "@type": "Country", name: "Sweden" }
      : {
          "@type": "City",
          name: "Göteborg",
          containedInPlace: { "@type": "Country", name: "Sweden" },
        },
    audience: {
      "@type": "Audience",
      audienceType,
    },
  };
  const cleaned = cleanOffer(offer ?? null);
  if (cleaned) node.offers = cleaned;
  return node;
};

const NOINDEX_PATHS = new Set(["/bekraftelse", "/en/confirmation"]);

const trimTrailingSlash = (path: string) => (path.length > 1 ? path.replace(/\/+$/, "") : path);

type PageSchemaInput = {
  pathname: string;
  lang: Lang;
  title: string;
  description: string;
};

const homeTrail = (lang: Lang): BreadcrumbTrailItem => ({
  name: lang === "sv" ? "Hem" : "Home",
  path: lang === "sv" ? "/" : "/en",
});

export async function buildPageStructuredData({
  pathname,
  lang,
  title,
  description,
}: PageSchemaInput): Promise<JsonLd | null> {
  const path = trimTrailingSlash(pathname);
  if (NOINDEX_PATHS.has(path)) return null;

  const t = translations[lang];
  const companyT = companyPages[lang];
  const drugT = drugTestingPages[lang];
  const url = absoluteUrl(path === "" ? "/" : path);
  const pagePath = path === "" ? "/" : path;
  const graph: JsonLd[] = [medicalClinicNode(lang), websiteNode()];

  let pageType: string | string[] = "WebPage";
  let trail: BreadcrumbTrailItem[] = [homeTrail(lang)];
  let serviceNode: JsonLd | null = null;
  let articleNode: JsonLd | null = null;
  let faqNode: JsonLd | null = null;
  let offerCatalog: JsonLd | null = null;
  let howTo: JsonLd | null = null;
  let extraNodes: JsonLd[] = [];

  // Home
  if (pagePath === "/" || pagePath === "/en") {
    trail = [homeTrail(lang)];
    const popular = POPULAR_SLUGS.map((slug) => certificates.find((c) => c.slug === slug)).filter(
      (c): c is (typeof certificates)[number] => Boolean(c),
    );
    offerCatalog = offerCatalogNode(
      url,
      lang === "sv" ? "Mest efterfrågade läkarintyg" : "Most requested medical certificates",
      popular.map((cert) => ({
        name: cert.title[lang],
        url: absoluteUrl(certificatePathFor(cert.slug, lang)),
      })),
    );
  }

  // Static pages
  else if (pagePath === "/kontakt" || pagePath === "/en/contact") {
    pageType = "ContactPage";
    trail = [homeTrail(lang), { name: t.nav.contact, path: pagePath }];
  } else if (pagePath === "/om-oss" || pagePath === "/en/about-us") {
    pageType = "AboutPage";
    trail = [homeTrail(lang), { name: t.about.eyebrow, path: pagePath }];
  } else if (pagePath === "/integritetspolicy" || pagePath === "/en/privacy-policy") {
    trail = [homeTrail(lang), { name: t.footer.privacy, path: pagePath }];
  } else if (pagePath === "/cookie-policy" || pagePath === "/en/cookie-policy") {
    trail = [homeTrail(lang), { name: t.footer.cookies, path: pagePath }];
  } else if (pagePath === "/anvandarvillkor" || pagePath === "/en/terms-of-use") {
    trail = [homeTrail(lang), { name: t.footer.terms, path: pagePath }];
  } else if (pagePath === "/fragor-och-svar" || pagePath === "/en/faq") {
    pageType = "FAQPage";
    trail = [homeTrail(lang), { name: t.nav.faq, path: pagePath }];
    faqNode = faqPageNode(
      url,
      t.faq.sections.flatMap((section) => section.items.map((item) => ({ q: item.q, a: item.a }))),
    );
  } else if (pagePath === "/lakarintyg" || pagePath === "/en/lakarintyg") {
    trail = [homeTrail(lang), { name: t.nav.certificates, path: pagePath }];
    offerCatalog = offerCatalogNode(
      url,
      lang === "sv" ? "Alla läkarintyg" : "All medical certificates",
      certificates.map((cert) => ({
        name: cert.title[lang],
        url: absoluteUrl(certificatePathFor(cert.slug, lang)),
      })),
    );
  } else if (pagePath === "/boka-tid" || pagePath === "/en/book-appointment") {
    trail = [homeTrail(lang), { name: t.booking.title, path: pagePath }];
  } else if (pagePath === "/guider" || pagePath === "/en/guides") {
    trail = [homeTrail(lang), { name: lang === "sv" ? "Guider" : "Guides", path: pagePath }];
  } else if (pagePath === "/sa-fungerar-det" || pagePath === "/en/how-it-works") {
    trail = [homeTrail(lang), { name: t.howItWorks.eyebrow, path: pagePath }];
    howTo = howToNode(url, t.howItWorks.title, t.howItWorks.subtitle, t.howItWorks.steps);
  } else if (pagePath === "/infor-besoket" || pagePath === "/en/before-visit") {
    trail = [homeTrail(lang), { name: t.nav.visit, path: pagePath }];
    howTo = howToNode(url, t.visit.title, t.visit.eyebrow, t.visit.steps);
  } else if (pagePath === "/halsokontroller" || pagePath === "/en/health-checks") {
    trail = [homeTrail(lang), { name: t.nav.checkups, path: pagePath }];
    serviceNode = serviceShell({
      url,
      name: t.nav.checkups,
      description: description,
      audienceType: lang === "sv" ? "Privatpersoner" : "Private individuals",
    });
  } else if (pagePath === "/provtagning" || pagePath === "/en/sample-collection") {
    trail = [
      homeTrail(lang),
      { name: lang === "sv" ? "Provtagning" : "Sample collection", path: pagePath },
    ];
    serviceNode = serviceShell({
      url,
      name: lang === "sv" ? "Provtagning" : "Sample collection",
      description,
      audienceType: lang === "sv" ? "Privatpersoner och företag" : "Individuals and companies",
      areaSweden: false,
    });
  } else if (pagePath === "/starta-arende" || pagePath === "/en/start-case") {
    trail = [
      homeTrail(lang),
      { name: lang === "sv" ? "Starta ärende" : "Start case", path: pagePath },
    ];
  }

  // Company pages
  else if (pagePath.startsWith("/foretag") || pagePath.startsWith("/en/companies")) {
    const companyHub = companyPaths.hub[lang];
    trail = [homeTrail(lang), { name: t.nav.company, path: companyHub }];
    if (pagePath !== companyHub) {
      trail.push({ name: stripBrandSuffix(title), path: pagePath });
    }

    const companyAudience = lang === "sv" ? "Företag och arbetsgivare" : "Companies and employers";

    if (pagePath === companyPaths.hub[lang]) {
      serviceNode = serviceShell({
        url,
        name: t.companies.title,
        description: t.companies.subtitle,
        audienceType: companyAudience,
      });
      offerCatalog = offerCatalogNode(
        url,
        lang === "sv" ? "Företagstjänster" : "Company services",
        [
          {
            name: companyT.nav.arbetsmiljo,
            url: absoluteUrl(companyPaths.arbetsmiljo[lang]),
          },
          {
            name: companyT.nav.drogtestning,
            url: absoluteUrl(companyPaths.drogtestning[lang]),
          },
          {
            name: companyT.nav.tjanster,
            url: absoluteUrl(companyPaths.tjanster[lang]),
          },
        ],
      );
    } else if (pagePath === companyPaths.arbetsmiljo[lang]) {
      serviceNode = serviceShell({
        url,
        name: companyT.arbetsmiljo.title,
        description: companyT.arbetsmiljo.subtitle,
        audienceType: companyAudience,
        areaSweden: false,
      });
      offerCatalog = offerCatalogNode(
        url,
        lang === "sv" ? "Arbetsmiljökontroller" : "Occupational health examinations",
        Object.entries(workEnvironmentLanding).map(([slug, landing]) => ({
          name: landing.title[lang],
          url: absoluteUrl(certificatePathFor(slug, lang)),
        })),
      );
    } else if (pagePath === companyPaths.drogtestning[lang]) {
      serviceNode = serviceShell({
        url,
        name: companyT.drogtestning.title,
        description: companyT.drogtestning.subtitle,
        audienceType: companyAudience,
      });
    } else if (pagePath === companyPaths.tjanster[lang]) {
      offerCatalog = offerCatalogNode(
        url,
        lang === "sv" ? "Alla företagstjänster" : "All company services",
        certificates.slice(0, 40).map((cert) => ({
          name: cert.title[lang],
          url: absoluteUrl(certificatePathFor(cert.slug, lang)),
        })),
      );
    } else if (pagePath === companyPaths.kontakt[lang]) {
      pageType = "ContactPage";
    }
  }

  // Drug-testing pages
  else if (pagePath.startsWith("/drogtestning") || pagePath.startsWith("/en/drug-testing")) {
    const hub = drugTestingPaths.hub[lang];
    trail = [homeTrail(lang), { name: t.nav.drugTesting, path: hub }];
    if (pagePath !== hub) {
      trail.push({ name: stripBrandSuffix(title), path: pagePath });
    }

    const privateAudience = lang === "sv" ? "Privatpersoner" : "Private individuals";

    if (pagePath === hub) {
      serviceNode = serviceShell({
        url,
        name: drugT.hub.title,
        description: drugT.hub.subtitle,
        audienceType: privateAudience,
        areaSweden: false,
      });
      offerCatalog = offerCatalogNode(
        url,
        lang === "sv" ? "Privata drogprovspaket" : "Private drug test packages",
        (["saliv", "urin"] as const).map((key) => {
          const pack = privateDrugPackages[key];
          return {
            name: pack.label[lang],
            url: absoluteUrl(
              key === "saliv" ? drugTestingPaths.bokaSaliv[lang] : drugTestingPaths.bokaUrin[lang],
            ),
          };
        }),
      );
      extraNodes = (["saliv", "urin"] as const).map((key) => {
        const pack = privateDrugPackages[key];
        const packUrl = absoluteUrl(
          key === "saliv" ? drugTestingPaths.bokaSaliv[lang] : drugTestingPaths.bokaUrin[lang],
        );
        return serviceShell({
          url: packUrl,
          name: pack.label[lang],
          description: pack.description[lang],
          offer: parsePriceOffer(pack.price[lang], packUrl, lang),
          audienceType: privateAudience,
          areaSweden: false,
        });
      });
    } else if (pagePath === drugTestingPaths.privat[lang]) {
      serviceNode = serviceShell({
        url,
        name: drugT.privat.title,
        description: drugT.privat.subtitle,
        audienceType: privateAudience,
        areaSweden: false,
      });
    } else if (pagePath === drugTestingPaths.forIntyg[lang]) {
      serviceNode = serviceShell({
        url,
        name: drugT.forIntyg.title,
        description: drugT.forIntyg.subtitle,
        audienceType: privateAudience,
      });
    } else {
      const bookMatch = pagePath.match(/\/(?:boka|book)\/([^/]+)$/);
      const sampleKey = bookMatch ? drugSampleFromPathSlug(bookMatch[1]) : null;
      if (sampleKey) {
        const pack = privateDrugPackages[sampleKey];
        serviceNode = serviceShell({
          url,
          name: pack.label[lang],
          description: pack.intro[lang],
          offer: parsePriceOffer(pack.price[lang], url, lang),
          audienceType: privateAudience,
          areaSweden: false,
        });
      }
    }
  }

  // Certificate detail
  const svCert = pagePath.match(/^\/lakarintyg\/([^/]+)$/);
  const enCert = pagePath.match(/^\/en\/medical-certificates\/([^/]+)$/);
  const certSlugParam = svCert?.[1] ?? enCert?.[1];
  if (certSlugParam) {
    const internal = findCertificateBySlug(certSlugParam, lang);
    const cert = internal ? certificates.find((c) => c.slug === internal) : null;
    if (cert) {
      const certPath = certificatePathFor(cert.slug, lang);
      const listPath = lang === "sv" ? "/lakarintyg" : "/en/lakarintyg";
      trail = [
        homeTrail(lang),
        { name: t.nav.certificates, path: listPath },
        { name: cert.title[lang], path: certPath },
      ];

      const digital = cert.modes.includes("digital");
      const channels: JsonLd[] = [
        {
          "@type": "ServiceChannel",
          name: lang === "sv" ? "Mottagningsbesök i Göteborg" : "Clinic appointment in Gothenburg",
          serviceLocation: { "@id": CLINIC_ID },
          serviceUrl: url,
        },
      ];
      if (digital) {
        channels.push({
          "@type": "ServiceChannel",
          name:
            lang === "sv"
              ? "Videobesök i hela Sverige när kraven tillåter"
              : "Video appointments across Sweden when requirements allow",
          serviceUrl: url,
        });
      }

      const offer = cleanOffer(parsePriceOffer(cert.price[lang], url, lang));

      serviceNode = {
        "@type": "Service",
        "@id": `${url}#service`,
        name: cert.title[lang],
        description: cert.description[lang],
        url,
        provider: { "@id": CLINIC_ID },
        category: t.categories[cert.category],
        serviceType: lang === "sv" ? "Läkarintyg" : "Medical certificate",
        areaServed: digital
          ? { "@type": "Country", name: "Sweden" }
          : {
              "@type": "City",
              name: "Göteborg",
              containedInPlace: { "@type": "Country", name: "Sweden" },
            },
        availableChannel: channels,
        audience: {
          "@type": "Audience",
          audienceType: lang === "sv" ? "Privatpersoner" : "Private individuals",
        },
        ...(offer ? { offers: offer } : {}),
      };

      const { getCertDetails } = await import("@/data/certificateDetails");
      const details = getCertDetails(cert.slug);
      const faqItems = details?.faq?.length
        ? details.faq.map((item) => ({ q: item.q[lang], a: item.a[lang] }))
        : certFaqGeneric(lang);
      faqNode = faqPageNode(url, faqItems);

      if (details?.steps?.[lang]?.length) {
        howTo = howToNode(
          url,
          lang === "sv" ? "Så här fungerar det" : "How it works",
          cert.title[lang],
          details.steps[lang].map((text, index) => ({
            title: `${index + 1}`,
            text,
          })),
        );
      }
    }
  }

  // Guide detail
  const svGuide = pagePath.match(/^\/guider\/([^/]+)\/([^/]+)$/);
  const enGuide = pagePath.match(/^\/en\/guides\/([^/]+)\/([^/]+)$/);
  const guideCat = svGuide?.[1] ?? enGuide?.[1];
  const guideSlug = svGuide?.[2] ?? enGuide?.[2];
  if (guideCat && guideSlug) {
    const route = findGuideRoute(guideCat, guideSlug);
    if (route) {
      const guidesHub = lang === "sv" ? "/guider" : "/en/guides";
      const articlePath = guideHref(route.category, route.slug, lang);
      const headline = stripBrandSuffix(title);
      trail = [
        homeTrail(lang),
        { name: lang === "sv" ? "Guider" : "Guides", path: guidesHub },
        { name: headline, path: articlePath },
      ];
      articleNode = {
        "@type": "Article",
        "@id": `${url}#article`,
        headline,
        description,
        inLanguage: lang === "en" ? "en" : "sv",
        url,
        mainEntityOfPage: { "@id": `${url}#webpage` },
        author: { "@id": CLINIC_ID },
        publisher: {
          "@id": CLINIC_ID,
          name: "Intygsläkaren",
          logo: {
            "@type": "ImageObject",
            url: `${SCHEMA_ORIGIN}/intygslakaren-og-image.png`,
          },
        },
        image: `${SCHEMA_ORIGIN}/intygslakaren-og-image.png`,
      };
    }
  }

  const isKnown =
    trail.length > 1 ||
    pagePath === "/" ||
    pagePath === "/en" ||
    Boolean(serviceNode) ||
    Boolean(articleNode) ||
    Boolean(faqNode);

  if (!isKnown && trail.length === 1 && pagePath !== "/" && pagePath !== "/en") {
    const knownPrefixes = [
      "/lakarintyg",
      "/en/lakarintyg",
      "/en/medical-certificates",
      "/guider",
      "/en/guides",
      "/foretag",
      "/en/companies",
      "/drogtestning",
      "/en/drug-testing",
      "/kontakt",
      "/en/contact",
      "/om-oss",
      "/en/about-us",
      "/integritetspolicy",
      "/en/privacy-policy",
      "/cookie-policy",
      "/en/cookie-policy",
      "/anvandarvillkor",
      "/en/terms-of-use",
      "/boka-tid",
      "/en/book-appointment",
      "/fragor-och-svar",
      "/en/faq",
      "/sa-fungerar-det",
      "/en/how-it-works",
      "/infor-besoket",
      "/en/before-visit",
      "/halsokontroller",
      "/en/health-checks",
      "/provtagning",
      "/en/sample-collection",
      "/starta-arende",
      "/en/start-case",
    ];
    if (!knownPrefixes.some((prefix) => pagePath === prefix || pagePath.startsWith(`${prefix}/`))) {
      return null;
    }
  }

  const webPage: JsonLd = {
    "@type": pageType,
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: lang === "en" ? "en" : "sv",
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": CLINIC_ID },
    publisher: { "@id": CLINIC_ID },
  };

  if (serviceNode) webPage.mainEntity = { "@id": `${url}#service` };
  else if (articleNode) webPage.mainEntity = { "@id": `${url}#article` };
  else if (howTo) webPage.mainEntity = { "@id": `${url}#howto` };

  graph.push(webPage);

  const crumbs = breadcrumbNode(url, trail);
  if (crumbs) graph.push(crumbs);
  if (serviceNode) graph.push(serviceNode);
  if (articleNode) graph.push(articleNode);
  if (offerCatalog) {
    graph.push(offerCatalog);
    // Link catalog from the clinic entity already in the graph.
    (graph[0] as JsonLd).hasOfferCatalog = { "@id": offerCatalog["@id"] };
  }
  if (howTo) graph.push(howTo);
  for (const node of extraNodes) graph.push(node);

  if (faqNode) {
    if (pageType === "FAQPage") {
      webPage.mainEntity = faqNode.mainEntity;
    } else {
      graph.push(faqNode);
    }
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
